import { useMemo, useState } from 'react';
import { PlusCircle, Edit, Trash2, Calendar, Clock, Wallet, Tag } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import ExpenseForm from './ExpenseForm';
import { formatCurrency, getCurrentRomeTime } from '../utils/formatters';
import { FREQUENCY_OPTIONS, FREQUENCY_LABELS, sanitizeStoreName } from '../features/recurring/recurringLogic';

const DEFAULT_FREQUENCY = FREQUENCY_OPTIONS[3]?.value || FREQUENCY_OPTIONS[0]?.value || 'monthly';

const formatOccurrence = (isoString) => {
  if (!isoString) {
    return 'Data non disponibile';
  }
  try {
    return format(new Date(isoString), 'EEEE dd MMMM yyyy - HH:mm', { locale: it });
  } catch (error) {
    return 'Data non valida';
  }
};

const getTimeUntilOccurrence = (isoString) => {
  if (!isoString) {
    return '';
  }

  try {
    const target = new Date(isoString);
    const now = getCurrentRomeTime();
    const diffMs = target.getTime() - now.getTime();

    if (diffMs <= 0) {
      return 'In attesa di generazione';
    }

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 60) {
      return `Tra ${diffMinutes} minuti`;
    }

    if (diffHours < 24) {
      return `Tra ${diffHours} ore`;
    }

    if (diffDays < 7) {
      return `Tra ${diffDays} giorni`;
    }

    if (diffDays < 30) {
      const weeks = Math.ceil(diffDays / 7);
      return `Tra ${weeks} settimana${weeks > 1 ? 'e' : ''}`;
    }

    if (diffDays < 365) {
      const months = Math.ceil(diffDays / 30);
      return `Tra ${months} mese${months > 1 ? 'i' : ''}`;
    }

    const years = Math.ceil(diffDays / 365);
    return `Tra ${years} anno${years > 1 ? 'i' : ''}`;
  } catch (error) {
    return '';
  }
};

const RecurringTransactions = ({
  recurringTransactions = [],
  categories = { expense: [], income: [] },
  wallets = [],
  stores = [],
  existingPersons = [],
  onAddStore = async () => {},
  onCreate,
  onUpdate,
  onDelete
}) => {
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('expense');
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [activeRecurringId, setActiveRecurringId] = useState(null);
  const [editingRecurring, setEditingRecurring] = useState(null);
  const [formEditingItem, setFormEditingItem] = useState(null);
  const [recurringFrequency, setRecurringFrequency] = useState(DEFAULT_FREQUENCY);

  const sortedRecurring = useMemo(() => {
    return [...recurringTransactions].sort((a, b) => {
      const dateA = a.nextOccurrence ? new Date(a.nextOccurrence).getTime() : Infinity;
      const dateB = b.nextOccurrence ? new Date(b.nextOccurrence).getTime() : Infinity;
      return dateA - dateB;
    });
  }, [recurringTransactions]);

  const handleOpenCreate = (type = 'expense') => {
    setFormMode('create');
    setFormType(type);
    setActiveRecurringId(null);
    setEditingRecurring(null);
    setFormEditingItem(null);
    setRecurringFrequency(DEFAULT_FREQUENCY);
    setShowForm(true);
  };

  const handleOpenEdit = (entry) => {
    if (!entry) return;
    const type = entry.type === 'income' ? 'income' : 'expense';
    setFormMode('edit');
    setFormType(type);
    setActiveRecurringId(entry.id || entry.documentId || null);
    setEditingRecurring(entry);
    setRecurringFrequency(entry.frequency || DEFAULT_FREQUENCY);
    setFormEditingItem({
      id: entry.id || entry.documentId,
      amount: entry.amount?.toString() || '',
      category: entry.category || '',
      walletId: entry.walletId || '',
      store: entry.store || '',
      date: entry.nextOccurrence || entry.firstOccurrence || new Date().toISOString(),
      note: entry.note || '',
      isFutureTransaction: true,
      isDebtTransaction: Boolean(entry.isDebtTransaction),
      debtPersonName: entry.debtPersonName || '',
      debtType: entry.debtType || (type === 'income' ? 'credit' : 'debt')
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setFormMode('create');
    setActiveRecurringId(null);
    setEditingRecurring(null);
    setFormEditingItem(null);
    setRecurringFrequency(DEFAULT_FREQUENCY);
  };

  const getWalletName = (walletId) => {
    if (walletId == null) return 'Conto non trovato';
    const wallet = wallets.find(w => w && (w.id === walletId || w.name === walletId));
    return wallet?.name || walletId || 'Conto non trovato';
  };

  const handleDelete = async (transaction) => {
    try {
      await onDelete?.(transaction);
    } catch (error) {
      console.error('Errore durante l\'eliminazione della transazione ripetuta:', error);
    }
  };

  const handleFormSubmit = async (submissionData) => {
    if (!submissionData) return;

    const numericAmount = Number(submissionData.amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      return;
    }

    const normalizedStore = sanitizeStoreName(submissionData.store, formType);
    const isDebtTransaction = Boolean(submissionData.isDebtTransaction);
    const resolvedDebtType = submissionData.debtType ||
      (formType === 'income' ? 'credit' : 'debt');
    const trimmedPersonName = submissionData.debtPersonName?.trim() || '';

    if (isDebtTransaction && !trimmedPersonName) {
      return;
    }
    const payload = {
      type: formType,
      amount: numericAmount,
      category: submissionData.category,
      walletId: submissionData.walletId,
      store: normalizedStore,
      frequency: recurringFrequency,
      nextOccurrence: submissionData.date,
      note: submissionData.note?.trim() || '',
      isDebtTransaction,
      active: true
    };

    if (isDebtTransaction) {
      payload.debtPersonName = trimmedPersonName;
      payload.debtType = resolvedDebtType === 'credit' ? 'credit' : 'debt';
    } else {
      delete payload.debtPersonName;
      delete payload.debtType;
    }

    if (formMode === 'create') {
      payload.firstOccurrence = submissionData.date;
    } else if (editingRecurring) {
      payload.firstOccurrence = editingRecurring.firstOccurrence || submissionData.date;
    }

    try {
      if (formMode === 'edit' && activeRecurringId) {
        await onUpdate?.(activeRecurringId, payload);
      } else {
        await onCreate?.(payload);
      }
      handleCloseForm();
    } catch (error) {
      console.error('Errore durante il salvataggio della transazione ripetuta:', error);
    }
  };

  const selectedWalletId = formEditingItem?.walletId || wallets[0]?.id || '';
  const availableCategories = formType === 'income'
    ? (categories.income || [])
    : (categories.expense || []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4 max-h-[60vh]">
        {sortedRecurring.length === 0 ? (
          <div className="text-center py-10">
            <RepeatIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Nessuna transazione ripetuta
            </h4>
            <p className="text-gray-600 dark:text-gray-400">
              Crea la tua prima transazione ripetuta per automatizzare entrate e uscite.
            </p>
          </div>
        ) : (
          <div className="space-y-3 pb-4">
            {sortedRecurring.map(transaction => (
              <div
                key={transaction.id || transaction.documentId}
                className={`p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm transition-all duration-200 ${
                  transaction.type === 'income'
                    ? 'bg-green-50 dark:bg-green-900/10 border-l-4 border-l-green-500'
                    : 'bg-red-50 dark:bg-red-900/10 border-l-4 border-l-red-500'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-lg font-semibold ${
                          transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {formatCurrency(Number(transaction.amount) || 0)}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-white/60 dark:bg-white/10 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600">
                        {transaction.type === 'income' ? 'Entrata' : 'Spesa'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <Tag className="w-4 h-4 text-gray-500" />
                      <span>{transaction.category}</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <Wallet className="w-4 h-4 text-gray-500" />
                      <span>{getWalletName(transaction.walletId)}</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span>{formatOccurrence(transaction.nextOccurrence)}</span>
                    </div>

                    <div className="flex items-center gap-2 text-xs font-semibold text-purple-600 dark:text-purple-300">
                      <Clock className="w-4 h-4" />
                      <span>{FREQUENCY_LABELS[transaction.frequency] || 'Frequenza sconosciuta'}</span>
                      <span className="text-orange-600 dark:text-orange-400">
                        {getTimeUntilOccurrence(transaction.nextOccurrence)}
                      </span>
                    </div>

                    {transaction.lastOccurrence && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Ultima generazione: {formatOccurrence(transaction.lastOccurrence)}
                      </p>
                    )}

                    {transaction.note && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                        “{transaction.note}”
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => handleOpenEdit(transaction)}
                      className="p-2 text-gray-600 hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                      title="Modifica"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(transaction)}
                      className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Elimina"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-900">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleOpenCreate('expense')}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-medium shadow-sm"
          >
            <PlusCircle className="w-4 h-4" />
            Nuova uscita
          </button>
          <button
            type="button"
            onClick={() => handleOpenCreate('income')}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors font-medium shadow-sm"
          >
            <PlusCircle className="w-4 h-4" />
            Nuova entrata
          </button>
        </div>
      </div>

      {showForm && (
        <ExpenseForm
          isOpen={showForm}
          onSubmit={handleFormSubmit}
          onClose={handleCloseForm}
          type={formType}
          editingItem={formEditingItem}
          stores={stores}
          categories={availableCategories}
          wallets={wallets}
          selectedWalletId={selectedWalletId}
          onAddStore={onAddStore}
          existingPersons={existingPersons}
          recurringOptions={{
            frequency: recurringFrequency,
            onFrequencyChange: setRecurringFrequency,
            options: FREQUENCY_OPTIONS
          }}
        />
      )}
    </div>
  );
};

const RepeatIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="17 1 21 5 17 9"></polyline>
    <path d="M3 11V9a4 4 0 0 1 4-4h14"></path>
    <polyline points="7 23 3 19 7 15"></polyline>
    <path d="M21 13v2a4 4 0 0 1-4 4H3"></path>
  </svg>
);

export default RecurringTransactions;
